import { useEffect, useState } from "react";
import {
  getAllTeamsApi,
  createTeamApi,
  getTeamDetailsApi,
  getTeamTasksApi,
  createTeamTaskApi,
  getTeamAnalyticsApi,
  getTeamActivityApi,
  getDepartmentsApi,
  getEmployeesApi,
} from "../../../app/api";

export default function TeamTasks() {
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamDetails, setTeamDetails] = useState(null);
  const [teamTasks, setTeamTasks] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [activity, setActivity] = useState([]);

  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [showCreateTask, setShowCreateTask] = useState(false);

  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);

  /* ================= LOAD INITIAL DATA ================= */

  useEffect(() => {
    loadTeams();
    loadDepartments();
    loadEmployees();
  }, []);

  const loadTeams = async () => {
    const res = await getAllTeamsApi();
    if (res.success) setTeams(res.data);
  };

  const loadDepartments = async () => {
    const res = await getDepartmentsApi();
    if (res.success) setDepartments(res.data);
  };

  const loadEmployees = async () => {
    const res = await getEmployeesApi();
    if (res.success) setEmployees(res.data);
  };

  /* ================= LOAD SELECTED TEAM DATA ================= */

  useEffect(() => {
    if (selectedTeam) {
      loadTeamDetails();
      loadTeamTasks();
      loadAnalytics();
      loadActivity();
    }
  }, [selectedTeam]);

  const loadTeamDetails = async () => {
    const res = await getTeamDetailsApi(selectedTeam);
    if (res.success) setTeamDetails(res.data);
  };

  const loadTeamTasks = async () => {
    const res = await getTeamTasksApi(selectedTeam);
    if (res.success) setTeamTasks(res.data);
  };

  const loadAnalytics = async () => {
    const res = await getTeamAnalyticsApi(selectedTeam);
    if (res.success) setAnalytics(res.data);
  };

  const loadActivity = async () => {
    const res = await getTeamActivityApi(selectedTeam);
    if (res.success) setActivity(res.data);
  };

  /* ================= CREATE TEAM ================= */

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);

    const payload = {
      name: form.get("name"),
      department_id: Number(form.get("department_id")),
      members: Array.from(
        e.target.querySelectorAll("input[name='members']:checked")
      ).map((el) => Number(el.value)),
    };

    await createTeamApi(payload);
    setShowCreateTeam(false);
    loadTeams();
  };

  /* ================= CREATE TEAM TASK ================= */

  const handleCreateTask = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);

    const payload = {
      title: form.get("title"),
      description: form.get("description"),
      priority: form.get("priority"),
      due_date: form.get("due_date"),
      assigned_to: Array.from(
        e.target.querySelectorAll("input[name='assigned_to']:checked")
      ).map((el) => Number(el.value)),
    };

    await createTeamTaskApi(selectedTeam, payload);
    setShowCreateTask(false);
    loadTeamTasks();
  };

  return (
    <div className="flex h-[calc(100vh-80px)] bg-gray-100 mt-20">

      {/* ================= LEFT: TEAM LIST ================= */}
      <div className="w-1/4 bg-white border-r p-4 overflow-y-auto">
        <div className="flex justify-between mb-4">
          <h2 className="font-semibold">Teams</h2>
          <button
  onClick={() => setShowCreateTeam(true)}
  className="bg-purple-600 text-white px-3 py-1 rounded"
>
  + Create
</button>
        </div>

        {teams.map((team) => (
          <div
            key={team.id}
            onClick={() => setSelectedTeam(team.id)}
            className={`p-2 rounded cursor-pointer mb-2 ${
              selectedTeam === team.id
                ? "bg-purple-100"
                : "hover:bg-gray-100"
            }`}
          >
            {team.name}
          </div>
        ))}
      </div>

      {/* ================= RIGHT: TEAM DETAILS ================= */}
      <div className="flex-1 p-6 overflow-y-auto">
        {!selectedTeam && <p>Select a team to view details</p>}

        {teamDetails && (
          <>
            <h2 className="text-xl font-bold mb-4">
              {teamDetails.name}
            </h2>

            {/* Members */}
            <div className="mb-6">
              <h3 className="font-semibold mb-2">Members</h3>
              <ul className="list-disc ml-6">
                {teamDetails.members.map((m) => (
                  <li key={m.id}>{m.name}</li>
                ))}
              </ul>
            </div>

            {/* Tasks */}
            <div className="mb-6">
              <div className="flex justify-between mb-2">
                <h3 className="font-semibold">Team Tasks</h3>
                <button
                  onClick={() => setShowCreateTask(true)}
                  className="bg-green-600 text-white px-3 py-1 rounded"
                >
                  + Add Task
                </button>
              </div>

              {teamTasks.map((task) => (
                <div
                  key={task.id}
                  className="p-3 bg-white rounded shadow mb-2"
                >
                  <div className="font-medium">{task.title}</div>
                  <div className="text-sm text-gray-500">
                    {task.status} | {task.priority}
                  </div>
                </div>
              ))}
            </div>

            {/* Analytics */}
            {analytics && (
              <div className="mb-6 bg-white p-4 rounded shadow">
                <h3 className="font-semibold mb-2">Analytics</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>Total: {analytics.total}</div>
                  <div>Completed: {analytics.completed}</div>
                  <div>In Progress: {analytics.in_progress}</div>
                  <div>Blocked: {analytics.blocked}</div>
                  <div>Overdue: {analytics.overdue}</div>
                </div>
              </div>
            )}

            {/* Activity */}
            <div className="bg-white p-4 rounded shadow">
              <h3 className="font-semibold mb-2">Activity</h3>
              {activity.map((a, i) => (
                <div key={i} className="text-sm mb-1">
                  {a.action} — {a.description}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ================= CREATE TEAM MODAL ================= */}
  {/* ================= CREATE TEAM DRAWER ================= */}
{showCreateTeam && (
  <div className="fixed inset-0 bg-black/40 flex justify-end">
    <form
      onSubmit={handleCreateTeam}
      className="bg-white p-6 w-[400px] h-full overflow-y-auto"
    >
      <h3 className="font-bold mb-3">Create Team</h3>

      <input
        name="name"
        placeholder="Team Name"
        className="w-full border p-2 mb-3"
        required
      />

      <select
        name="department_id"
        className="w-full border p-2 mb-3"
        required
      >
        <option value="">Select Department</option>
        {departments.map((d) => (
          <option key={d.id} value={d.id}>{d.name}</option>
        ))}
      </select>

      <div className="mb-3 max-h-32 overflow-y-auto border p-2">
        {employees.map((emp) => (
          <label key={emp.id} className="block text-sm">
            <input
              type="checkbox"
              name="members"
              value={emp.id}
              className="mr-2"
            />
            {emp.first_name} {emp.last_name}
          </label>
        ))}
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <button
          type="button"
          onClick={() => setShowCreateTeam(false)}
          className="px-3 py-1 bg-gray-400 text-white rounded"
        >
          Cancel
        </button>
        <button className="px-3 py-1 bg-purple-600 text-white rounded">
          Create
        </button>
      </div>
    </form>
  </div>
)}

      {/* ================= CREATE TASK MODAL ================= */}
      {showCreateTask && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center">
          <form
            onSubmit={handleCreateTask}
            className="bg-white p-6 rounded w-[400px]"
          >
            <h3 className="font-bold mb-3">Create Task</h3>

            <input
              name="title"
              placeholder="Title"
              className="w-full border p-2 mb-3"
              required
            />

            <textarea
              name="description"
              placeholder="Description"
              className="w-full border p-2 mb-3"
            />

            <select
              name="priority"
              className="w-full border p-2 mb-3"
              required
            >
              <option value="LOW">LOW</option>
              <option value="MEDIUM">MEDIUM</option>
              <option value="HIGH">HIGH</option>
            </select>

            <input
              type="date"
              name="due_date"
              className="w-full border p-2 mb-3"
              required
            />

            <div className="mb-3 max-h-32 overflow-y-auto border p-2">
              {teamDetails?.members.map((m) => (
                <label key={m.id} className="block text-sm">
                  <input
                    type="checkbox"
                    name="assigned_to"
                    value={m.id}
                    className="mr-2"
                  />
                  {m.name}
                </label>
              ))}
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowCreateTask(false)}
                className="px-3 py-1 bg-gray-400 text-white rounded"
              >
                Cancel
              </button>
              <button className="px-3 py-1 bg-green-600 text-white rounded">
                Create
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}